import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import {
  Button,
  Form,
  Typography,
  Row,
  Col,
  notification,
  Space,
  Input,
  Select
} from 'antd'
import * as Icons from '@ant-design/icons'
import { generatePath, useNavigate } from 'react-router-dom'
import produce from 'immer'
import styled from 'styled-components'

import { Queries } from 'admin/lib/Api'
import { BodyContentColumn } from 'admin/lib/Layouts'
import { useGlobalShortcut } from 'admin/lib/shortcuts'
import * as ROUTES from 'admin/routes'
import { Matchers } from 'admin/lib/Records'
import { Switch } from 'admin/lib/Switch'
import { AccountSelect } from 'admin/lib/EntityInputs'
import { SectionProvider, Section } from 'admin/lib/Forms'
import { FormProvider } from 'rc-field-form'

const { Option } = Select

const DEFAULT_USER = {
  accountEnabled: true,
  accountMembershipIds: [],
  accountAdminIds: [],
  projectSettings: []
}

export function UserEditForm({
  user,
  userAccountMappings,
  ssoIdps,
  isNew = false
}) {
  const [saveEnabled, setSaveEnabled] = useState(true)
  const navigate = useNavigate()
  const userUpsert = Queries.useUserUpsert(isNew)
  const accounts = Queries.useAccounts('global')

  //
  // Form Setup

  let formValues = useMemo(() => {
    if (user) {
      return transformUserToFormData(user, userAccountMappings)
    }

    return DEFAULT_USER
  }, [user, userAccountMappings])

  const [form] = Form.useForm()

  //
  // Callbacks

  const navigateToList = useCallback(() => {
    navigate(ROUTES.USERS.LIST)
  }, [navigate])

  const onSubmit = useCallback(
    async values => {
      setSaveEnabled(false)
      const workingKey = 'UPSERTING_USER'
      notification.info({
        key: workingKey,
        icon: <Icons.LoadingOutlined />,
        message: isNew ? 'Creating user' : 'Updating user',
        duration: 0
      })

      userUpsert.mutate(
        {
          user: transformFormDataToUser(isNew ? DEFAULT_USER : user, values)
        },
        {
          onSuccess: data => {
            notification.success({
              message: isNew
                ? 'Successfully created user'
                : 'Successfully updated user'
            })
            setSaveEnabled(true)
            if (isNew) {
              navigate(
                generatePath(ROUTES.USERS.EDIT, {
                  userId: data.userId
                })
              )
            }
          },
          onSettled: () => {
            notification.destroy(workingKey)
          },
          onError: error => {
            setSaveEnabled(true)

            notification.error({
              message: 'Error 😞',
              description: (
                <Typography.Text type="danger">
                  {JSON.stringify(error.response.data)}
                </Typography.Text>
              )
            })
          }
        }
      )
    },
    [isNew, userUpsert, user, navigate]
  )

  useGlobalShortcut(Matchers.form.save, () => onSubmit(form.submit()))

  const [showNotificationTable, setShowNotificationTable] = useState(
    formValues.notificationsEnabled
  )

  const [memberAccountIds, setMemberAccountIds] = useState(
    formValues.accountMembershipIds
  )

  const [ssoExternalIdEnabled, setSsoExternalIdEnabled] = useState(
    user?.ssoIdp != null
  )

  const [allNotificationsEnabled, setAllNotificationsEnabled] = useState(
    formValues.projectSettings.every(v => v.notificationsEnabled === true)
  )

  function toggleAllNotifications() {
    const newProjectSettings = formValues.projectSettings
    newProjectSettings.forEach(projectSetting => {
      projectSetting.notificationsEnabled = !allNotificationsEnabled
    })
    formValues.projectSettings = newProjectSettings
    setAllNotificationsEnabled(!allNotificationsEnabled)
    form.setFieldsValue(formValues)
  }

  const handleSelect = useCallback(
    (value, option) => {
      if (value == null) {
        form.setFieldsValue({ ssoExternalId: null })
        setSsoExternalIdEnabled(false)
      } else {
        setSsoExternalIdEnabled(true)
        const email = form.getFieldValue('email')
        form.setFieldsValue({ ssoExternalId: email })
      }
    },
    [form]
  )

  return (
    <FormProvider>
      <SectionProvider>
        <BodyContentColumn>
          <Form
            form={form}
            name="userEdit"
            initialValues={formValues}
            onFinish={onSubmit}
            layout="vertical"
            requiredMark={false}
          >
            <Section>
              {({ firstInputRef }) => (
                <>
                  <Typography.Title level={3}>
                    {isNew ? 'New User' : 'Edit User'}
                  </Typography.Title>

                  <Row style={{ width: '100%' }}>
                    <Form.Item
                      name="firstName"
                      label="First Name"
                      rules={[{ required: true }]}
                    >
                      <Input
                        firstInputRef={firstInputRef}
                        style={{ width: '300px' }}
                      />
                    </Form.Item>
                    <Form.Item
                      name="lastName"
                      label="Last Name"
                      rules={[{ required: true }]}
                    >
                      <Input style={{ width: '325px' }} />
                    </Form.Item>
                  </Row>

                  <Form.Item
                    name="email"
                    label="Email"
                    rules={[
                      {
                        required: true,
                        pattern: {
                          value: /@/,
                          message: 'Must be a valid email address'
                        }
                      }
                    ]}
                  >
                    <Input disabled={!isNew} />
                  </Form.Item>

                  <Row justify="space-between">
                    {' '}
                    <Col span={7}>
                      <Form.Item name="ssoIdp" label="SSO Provider">
                        <Select onSelect={handleSelect}>
                          <Select.Option value={null}>
                            Not an SSO User
                          </Select.Option>
                          {ssoIdps.map(sso => {
                            return (
                              <Select.Option value={sso.ssoidp}>
                                {sso.displayName}
                              </Select.Option>
                            )
                          })}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={16}>
                      <Form.Item
                        rules={[
                          {
                            required: ssoExternalIdEnabled,
                            message: 'SSO users must have an external Id set'
                          }
                        ]}
                        name="ssoExternalId"
                        label="SSO External Id"
                      >
                        <Input disabled={!ssoExternalIdEnabled} />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item
                    name="companyName"
                    label="Company Name"
                    rules={[{ required: true }]}
                  >
                    <Input />
                  </Form.Item>

                  <Form.Item
                    name="accountMembershipIds"
                    label="Account Assignments"
                  >
                    <AccountSelect
                      scope="global"
                      onChange={val => {
                        formValues = form.getFieldsValue()
                        formValues.accountMembershipIds = val
                        setMemberAccountIds(val)
                        formValues.accountAdminIds = adjustAdminIds(
                          formValues.accountAdminIds,
                          formValues.accountMembershipIds
                        )
                        formValues.projectSettings = rebuildprojectSettings(
                          user,
                          userAccountMappings,
                          formValues.projectSettings,
                          formValues.accountMembershipIds
                        )
                        form.setFieldsValue(formValues)
                      }}
                    />
                  </Form.Item>

                  <Form.Item
                    name="accountAdminIds"
                    label="Account Admin Assignments"
                  >
                    <Select
                      mode="multiple"
                      allowClear
                      showSearch
                      notFoundContent="Please assign user to some accounts to select admin access"
                      onChange={val => {
                        formValues = form.getFieldsValue()
                        formValues.accountAdminIds = val
                        form.setFieldsValue(formValues)
                      }}
                      filterOption={(input, option) =>
                        option.props.children
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                    >
                      {accounts.data
                        ?.filter(account =>
                          memberAccountIds.includes(account.accountId)
                        )
                        .map(account => (
                          <Option
                            key={account.accountId}
                            value={account.accountId}
                          >
                            {account.name}
                          </Option>
                        ))}
                    </Select>
                  </Form.Item>
                  <Form.Item name="accountEnabled" label="User Can Sign In">
                    <Switch />
                  </Form.Item>
                  <Form.Item
                    name="notificationsEnabled"
                    label="Notifications Enabled"
                  >
                    <Switch
                      onClick={value => setShowNotificationTable(value)}
                    />
                  </Form.Item>

                  {showNotificationTable &&
                    formValues.projectSettings.length > 0 && (
                      <>
                        <Button
                          onClick={() => toggleAllNotifications()}
                          style={{
                            marginLeft: '70%',
                            marginBottom: '20px',
                            width: '185px'
                          }}
                        >
                          {(allNotificationsEnabled ? 'Disable' : 'Enable') +
                            ' All Notifications'}
                        </Button>
                        <Form.List name="projectSettings">
                          {fields => (
                            <div class="ant-table-content">
                              <NotificationsTable>
                                <thead class="ant-table-thead">
                                  <tr>
                                    <th id="project" class="ant-table-cell">
                                      Project Name
                                    </th>
                                    <th id="account" class="ant-table-cell">
                                      Account Name
                                    </th>
                                    <th id="notif" class="ant-table-cell">
                                      Enabled/Disabled
                                    </th>
                                  </tr>
                                </thead>
                                <tbody class="ant-table-tbody">
                                  {fields.map((field, index) => (
                                    <tr
                                      key={field.key}
                                      class="ant-table-row ant-table-row-level-0"
                                    >
                                      <td id="project" class="ant-table-cell">
                                        {
                                          formValues.projectSettings[index]
                                            ?.projectName
                                        }
                                      </td>
                                      <td id="account" class="ant-table-cell">
                                        {
                                          formValues.projectSettings[index]
                                            ?.accountName
                                        }
                                      </td>
                                      <td id="notif" class="ant-table-cell">
                                        <Form.Item
                                          name={[index, 'notificationsEnabled']}
                                          rules={[{ required: true }]}
                                          style={{
                                            display: 'inline-block',
                                            margin: '0px auto',
                                            padding: 0
                                          }}
                                        >
                                          <Switch
                                            size="small"
                                            style={{ verticalAlign: 'middle' }}
                                          />
                                        </Form.Item>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </NotificationsTable>
                            </div>
                          )}
                        </Form.List>
                      </>
                    )}
                </>
              )}
            </Section>

            <Row justify="center">
              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  style={{ width: '8rem' }}
                  disabled={!saveEnabled}
                >
                  Save
                </Button>

                <Button onClick={navigateToList} style={{ width: '8rem' }}>
                  Back to list
                </Button>
              </Space>
            </Row>
          </Form>
        </BodyContentColumn>
      </SectionProvider>
    </FormProvider>
  )
}

UserEditForm.propTypes = {
  user: PropTypes.object,
  userAccountMappings: PropTypes.array,
  ssoIdps: PropTypes.array,
  isNew: PropTypes.bool
}

const NotificationsTable = styled.table`
  table-layout: auto;
  th,
  td {
    width: 100%;
  }

  thead tr th,
  tbody tr td {
    height: 10px;
    padding-top: 1px;
    padding-bottom: 1px;
    padding-left: 15px;
  }

  #account {
    width: 40%;
  }

  #project {
    width: 40%;
  }

  #notif {
    width: 20%;
    text-align: center;
  }
`

function adjustAdminIds(prevAdminIds, accountIds) {
  const adminIds = []

  prevAdminIds.forEach(id => {
    if (accountIds.includes(id)) {
      adminIds.push(id)
    }
  })

  return adminIds
}

function rebuildprojectSettings(
  user,
  userAccountMappings,
  prevProjectSettings,
  accountIds
) {
  const projectSettingsTable = []

  if (!user || !prevProjectSettings) {
    return projectSettingsTable
  }

  userAccountMappings.forEach(account => {
    account.projects.forEach(project => {
      const found = prevProjectSettings.find(
        projectSetting => projectSetting.projectId == project.projectId
      )

      if (accountIds.includes(account.accountId)) {
        projectSettingsTable.push({
          userId: user.userId,
          accountId: account.accountId,
          accountName: account.name,
          projectId: project.projectId,
          projectName: project.name,
          notificationsEnabled: found ? found.notificationsEnabled : false
        })
      }
    })
  })

  return projectSettingsTable
}

export function transformUserToFormData(apiUser, userAccountMappings) {
  const accountMembershipIds = []
  const accountAdminIds = []
  userAccountMappings.forEach(acc => {
    accountMembershipIds.push(acc.accountId)
    if (acc.isAdmin) {
      accountAdminIds.push(acc.accountId)
    }
  })
  const projectSettings = rebuildprojectSettings(
    apiUser,
    userAccountMappings,
    apiUser.projectSettings,
    accountMembershipIds
  )

  return {
    ...apiUser,
    accountMembershipIds: accountMembershipIds,
    accountAdminIds: accountAdminIds,
    projectSettings: projectSettings
  }
}

export function transformFormDataToUser(apiUser, values) {
  const accountSettings = []
  values.accountMembershipIds.forEach(id =>
    accountSettings.push({
      accountId: id,
      isAdmin: values.accountAdminIds.includes(id)
    })
  )
  return produce(apiUser, draft => {
    delete draft.accounts
    delete draft.accountMembershipIds
    delete draft.accountAdminIds
    draft.accountSettings = accountSettings
    Object.assign(draft, values)
  })
}
